// src/components/user/ComplaintForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Fade,
  Slide
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import MessageIcon from '@mui/icons-material/Message';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';

import { v4 as uuidv4 } from 'uuid';
import api from '../../services/api';

const ComplaintForm = () => {
  const navigate = useNavigate();
  console.log('ComplaintForm component mounted');

  // Dynamic data: categories, assemblies, districts, etc.
  const [category, setCategory] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [districts, setDistricts] = useState([]);
  
  // File upload limit from settings (default 5 MB)
  const [maxFileSizeMB, setMaxFileSizeMB] = useState(5);
  const MAX_FILE_SIZE = maxFileSizeMB * 1024 * 1024; // Convert MB to bytes

  // For generating complaint ID and submission token
  const [complaintId, setComplaintId] = useState(null);
  const [submissionToken, setSubmissionToken] = useState(null);

  // Form state (grouped fields)
  const [formData, setFormData] = useState({
    name: '',
    parentName: '',
    legislativeAssembly: '',
    district: '',
    panchayath: '',
    address: '',
    wardNo: '',
    mobileNumber: '',
    alternateMobileNumber: '',
    category: '',
    priority: 'Medium',
    idProofType: '',
    idNumber: '',
    reference: '',
    referredMobileNumber: '',
    subject: '',
    complaintDetails: '',
    remarks: ''
  });

  // File states for uploads and previews
  const [idProofFile, setIdProofFile] = useState(null);
  const [complaintPdfFile, setComplaintPdfFile] = useState(null);
  const [idProofPreview, setIdProofPreview] = useState(null);
  const [complaintPdfPreview, setComplaintPdfPreview] = useState(null);

  // Enhanced error & validation handling
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [networkError, setNetworkError] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openSmsStatusDialog, setOpenSmsStatusDialog] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsRefreshLoading, setSmsRefreshLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Duplicate detection dialog state
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // On mount: fetch submission token, categories, assemblies, and complaint ID.
  useEffect(() => {
    // Always generate a new token on form load to avoid duplicate key errors
    // This ensures each form submission attempt uses a unique token
    const token = uuidv4();
    sessionStorage.setItem('submissionToken', token);
    setSubmissionToken(token);

    api.get('/category')
      .then(response => setCategory(response.data))
      .catch(error => console.error('Error fetching category:', error));

    api.get('/assemblies')
      .then(response => setAssemblies(response.data))
      .catch(error => console.error('Error fetching assemblies:', error));

    api.get('/districts')
      .then(response => {
        // Ensure response.data is an array
        if (Array.isArray(response.data)) {
          setDistricts(response.data);
        } else {
          console.warn('Districts API returned non-array data:', response.data);
          setDistricts([]);
        }
      })
      .catch(error => {
        console.error('Error fetching districts:', error);
        // Ensure districts remains an empty array on error
        setDistricts([]);
      });

    // Fetch file upload limit from settings
    api.get('/settings')
      .then(response => {
        if (response.data && response.data.fileUploadLimitMB) {
          setMaxFileSizeMB(response.data.fileUploadLimitMB);
          console.log('📁 File upload limit loaded from settings:', response.data.fileUploadLimitMB, 'MB');
        }
      })
      .catch(error => {
        console.warn('Could not fetch file upload limit from settings, using default 5MB:', error);
        // Keep default 5MB on error
      });

    // Generate complaint ID
    const newComplaintId = 'new';
    setComplaintId(newComplaintId);
    sessionStorage.setItem('complaintId', newComplaintId);
  }, []);

  // Real-time validation on field change
  useEffect(() => {
    const newErrors = validateField();
    setErrors(newErrors);
  }, [formData, touched]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleAssemblyChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      legislativeAssembly: value,
      panchayath: '' // Reset panchayath when assembly changes
    }));
    setTouched(prev => ({ ...prev, legislativeAssembly: true, panchayath: false }));
  };

  const handleComplaintDetailsChange = (value) => {
    setFormData(prev => ({ ...prev, complaintDetails: value }));
    setTouched(prev => ({ ...prev, complaintDetails: true }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const fieldName = e.target.name;

    if (file) {
      // Validate file size
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxSizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
      
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: `File size (${fileSizeMB}MB) exceeds the maximum allowed size of ${maxSizeMB}MB`
        }));
        // Clear the file input
        e.target.value = '';
        return;
      }

      // Validate file type based on field name
      let allowedTypes = [];
      let errorMessage = '';
      
      if (fieldName === 'idProofFile') {
        // ID Proof can be PDF or images
        allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        errorMessage = 'ID Proof must be a PDF, JPEG, JPG, or PNG file';
      } else if (fieldName === 'complaintPdfFile') {
        // Complaint PDF must be PDF only
        allowedTypes = ['application/pdf'];
        errorMessage = 'Complaint PDF must be a PDF file. Other file types are not allowed';
      } else {
        // Default: PDF only
        allowedTypes = ['application/pdf'];
        errorMessage = 'Only PDF files are allowed for this field';
      }

      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: errorMessage
        }));
        // Clear the file input
        e.target.value = '';
        return;
      }

      // Clear previous errors
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });

      // Set file
      if (fieldName === 'idProofFile') {
        setIdProofFile(file);
        setIdProofPreview(URL.createObjectURL(file));
      } else if (fieldName === 'complaintPdfFile') {
        setComplaintPdfFile(file);
        setComplaintPdfPreview(URL.createObjectURL(file));
      }
    }
  };

  // Enhanced field validation
  const validateField = () => {
    const newErrors = {};

    // Name validation
    if (touched.name) {
      if (!formData.name || formData.name.trim() === '') {
        newErrors.name = 'Name is required';
      } else if (formData.name.length < 4) {
        newErrors.name = 'Name must be at least 4 characters long';
      } else if (formData.name.length > 20) {
        newErrors.name = 'Name must be less than 20 characters';
      }
    }

    // Parent Name validation (optional)
    if (touched.parentName && formData.parentName) {
      if (formData.parentName.length < 4) {
        newErrors.parentName = 'Parent name must be at least 4 characters long';
      } else if (formData.parentName.length > 20) {
        newErrors.parentName = 'Parent name must be less than 20 characters';
      }
    }

    // Legislative Assembly validation
    if (touched.legislativeAssembly) {
      if (!formData.legislativeAssembly) {
        newErrors.legislativeAssembly = 'Please select a Legislative Assembly';
      }
    }

    // Address validation (optional)
    if (touched.address && formData.address) {
      if (formData.address.length < 14) {
        newErrors.address = 'Address must be at least 14 characters long';
      } else if (formData.address.length > 700) {
        newErrors.address = 'Address must be less than 700 characters';
      }
    }

    // Mobile Number validation (optional)
    if (touched.mobileNumber && formData.mobileNumber) {
      const mobileRegex = /^[6-9]\d{9}$/;
      if (!mobileRegex.test(formData.mobileNumber)) {
        newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9';
      }
    }

    // Alternate Mobile Number validation
    if (touched.alternateMobileNumber && formData.alternateMobileNumber) {
      const mobileRegex = /^[6-9]\d{9}$/;
      if (!mobileRegex.test(formData.alternateMobileNumber)) {
        newErrors.alternateMobileNumber = 'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9';
      }
    }

    // Category validation
    if (touched.category) {
      if (!formData.category) {
        newErrors.category = 'Please select a category';
      }
    }

    // ID Proof Type validation (optional)
    // No validation needed if optional

    // ID Number validation (optional)
    if (touched.idNumber && formData.idNumber) {
      if (formData.idNumber.length < 3) {
        newErrors.idNumber = 'ID number must be at least 3 characters long';
      } else if (formData.idNumber.length > 50) {
        newErrors.idNumber = 'ID number must be less than 50 characters';
      }
    }

    // Reference validation (optional)
    if (touched.reference && formData.reference) {
      if (formData.reference.length < 4) {
        newErrors.reference = 'Reference must be at least 4 characters long';
      } else if (formData.reference.length > 100) {
        newErrors.reference = 'Reference must be less than 100 characters';
      }
    }

    // Referred Mobile Number validation
    if (touched.referredMobileNumber && formData.referredMobileNumber) {
      const mobileRegex = /^[6-9]\d{9}$/;
      if (!mobileRegex.test(formData.referredMobileNumber)) {
        newErrors.referredMobileNumber = 'Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9';
      }
    }

    // Subject validation
    if (touched.subject) {
      if (!formData.subject || formData.subject.trim() === '') {
        newErrors.subject = 'Subject is required';
      } else if (formData.subject.length < 10) {
        newErrors.subject = 'Subject must be at least 10 characters long';
      }
    }

    // Complaint Details validation
    if (touched.complaintDetails) {
      const plainText = formData.complaintDetails.replace(/<[^>]*>/g, '').trim();
      if (!plainText || plainText === '') {
        newErrors.complaintDetails = 'Complaint details are required';
      } else if (plainText.length < 10) {
        newErrors.complaintDetails = 'Complaint details must be at least 10 characters long';
      } else if (plainText.length > 30000) {
        newErrors.complaintDetails = 'Complaint details must be less than 30,000 characters';
      }
    }

    // Remarks validation
    if (touched.remarks && formData.remarks && formData.remarks.trim() !== '') {
      const plainText = formData.remarks.replace(/<[^>]*>/g, '').trim();
      if (plainText.length < 10) {
        newErrors.remarks = 'Remarks must be at least 10 characters long if provided';
      } else if (plainText.length > 3000) {
        newErrors.remarks = 'Remarks must be less than 3,000 characters';
      }
    }

    // Panchayath validation when assembly is selected
    if (touched.panchayath && formData.legislativeAssembly && !formData.panchayath) {
      newErrors.panchayath = 'Please select a Panchayath for the selected Assembly';
    }

    return newErrors;
  };

  const buildErrorMessage = (errorsObj) => {
    return Object.entries(errorsObj)
      .map(([field, error]) => `• ${field.charAt(0).toUpperCase() + field.slice(1)}: ${error}`)
      .join('\n');
  };

  const refreshSmsStatus = async (complaintId) => {
    setSmsRefreshLoading(true);
    try {
      const response = await api.get(`/complaints/${complaintId}/sms-status`);
      setSmsStatus(prev => ({
        ...prev,
        latestStatus: response.data
      }));
    } catch (error) {
      console.error('Error refreshing SMS status:', error);
    } finally {
      setSmsRefreshLoading(false);
    }
  };

  // Enhanced validation function for form submission
  const validateForm = () => {
    // Mark all fields as touched for validation
    const allFields = Object.keys(formData);
    setTouched(prev => {
      const newTouched = { ...prev };
      allFields.forEach(field => {
        newTouched[field] = true;
      });
      return newTouched;
    });

    // Return current errors
    return validateField();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNetworkError('');
    setIsSubmitting(true);

    // Enhanced validation
    const formErrors = validateForm();
    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      // Show error message to user
      const errorMessage = buildErrorMessage(formErrors);
      setNetworkError(`Please fix the following validation errors:\n\n${errorMessage}`);
      setOpenErrorDialog(true);
      setIsSubmitting(false);
      return;
    }

    // Duplicate detection: check for open complaints from the same mobile number
    if (formData.mobileNumber && formData.mobileNumber.trim() !== '') {
      try {
        const params = { mobileNumber: formData.mobileNumber.trim() };
        if (formData.category) {
          params.category = formData.category;
        }
        const dupResponse = await api.get('/complaints/check-duplicate', { params });
        if (dupResponse.data && dupResponse.data.hasDuplicates) {
          setDuplicateInfo(dupResponse.data);
          setOpenDuplicateDialog(true);
          // Keep isSubmitting true while the dialog is open to prevent double-submit
          return;
        }
      } catch (dupError) {
        // Never block submission on the duplicate check
        console.warn('Duplicate check failed, proceeding with submission:', dupError);
      }
    }

    await performSubmit();
  };

  const handleDuplicateCancel = () => {
    setOpenDuplicateDialog(false);
    setDuplicateInfo(null);
    setIsSubmitting(false);
  };

  const handleDuplicateSubmitAnyway = async () => {
    setOpenDuplicateDialog(false);
    setDuplicateInfo(null);
    await performSubmit();
  };

  const performSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // IMPORTANT: Get the complaint ID from backend BEFORE uploading files
      // This ensures multer uses the correct complaint ID for file paths
      let actualComplaintId = complaintId;
      if (!complaintId || complaintId === 'new') {
        // Retry function for network errors (handles 525, CORS issues on first request)
        const fetchComplaintIdWithRetry = async (retryCount = 0, maxRetries = 2) => {
          try {
            console.log(`📋 Fetching new complaint ID from backend... (attempt ${retryCount + 1}/${maxRetries + 1})`);
            const idResponse = await api.get('/complaints/new-id', {
              headers: { Authorization: `Bearer ${token}` }
            });
            actualComplaintId = idResponse.data.complaintId;
            console.log('✅ Got complaint ID from backend:', actualComplaintId);
            setComplaintId(actualComplaintId);
            sessionStorage.setItem('complaintId', actualComplaintId);
            return actualComplaintId;
          } catch (idError) {
            console.error(`❌ Error fetching complaint ID (attempt ${retryCount + 1}):`, idError);
            
            // Retry on network errors (CORS, 525, etc.)
            if ((idError.code === 'ERR_NETWORK' || !idError.response) && retryCount < maxRetries) {
              console.warn(`Retrying complaint ID fetch after ${(retryCount + 1) * 1000}ms...`);
              await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
              return fetchComplaintIdWithRetry(retryCount + 1, maxRetries);
            } else {
              // Final attempt failed or non-network error - continue with temporary ID
              console.warn('⚠️ No valid complaint ID available; multer will use temporary ID.');
              return null;
            }
          }
        };
        
        const fetchedId = await fetchComplaintIdWithRetry();
        if (fetchedId) {
          actualComplaintId = fetchedId;
        }
      }
      
      const data = new FormData();
      data.append('submissionToken', submissionToken);
      
      // Clean up form data - remove empty strings for optional fields
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // Skip empty strings for optional fields (convert to null/undefined)
        // Optional fields: parentName, address, mobileNumber, idProofType, idNumber, reference, district, wardNo, alternateMobileNumber, referredMobileNumber
        const optionalFields = ['parentName', 'address', 'mobileNumber', 'idProofType', 'idNumber', 'reference', 'district', 'wardNo', 'alternateMobileNumber', 'referredMobileNumber'];
        
        if (optionalFields.includes(key)) {
          // Only append if value is not empty
          if (value && value.trim() !== '') {
            data.append(key, value.trim());
          }
          // Don't append empty strings for optional fields
        } else {
          // Required fields - always append
          data.append(key, value);
        }
      });
      if (idProofFile) data.append('idProofFile', idProofFile);
      if (complaintPdfFile) data.append('complaintPdfFile', complaintPdfFile);

      const headers = {
        Authorization: `Bearer ${token}`
        // DO NOT set 'Content-Type' for FormData - browser will set it automatically with boundary
      };
      
      // Always send the complaint ID header if we have it
      if (actualComplaintId && actualComplaintId !== 'new') {
        headers['x-complaint-id'] = actualComplaintId;
        console.log('📋 Sending x-complaint-id header:', actualComplaintId);
      } else {
        console.warn('⚠️ No valid complaint ID available; multer will use temporary ID.');
      }

      const response = await api.post('/complaints', data, { headers });
      
      // Store SMS status for feedback
      console.log('Full response:', response.data);
      setSmsStatus(response.data.smsStatus);
      
      // Success feedback
      setOpenSmsStatusDialog(true);
      sessionStorage.removeItem('submissionToken');
      sessionStorage.removeItem('complaintId');
      
      // Fetch latest SMS status after a short delay to allow for processing
      setTimeout(() => {
        if (response.data.complaintId) {
          refreshSmsStatus(response.data.complaintId);
        }
      }, 2000); // Wait 2 seconds for SMS processing
      
    } catch (error) {
      console.error('Error submitting complaint:', error);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      // Clear submission token on error to allow retry with new token
      // This prevents duplicate key errors if user retries submission
      sessionStorage.removeItem('submissionToken');
      sessionStorage.removeItem('complaintId');
      
      if (error.response) {
        const data = error.response.data;
        const status = error.response.status;
        
        // Handle different HTTP status codes
        if (status === 400) {
          // Bad Request - usually validation errors
          if (data.error) {
            // Check for file upload errors
            if (data.error.includes('File size') || data.error.includes('exceeds the maximum')) {
              errorMessage = `File Upload Error: ${data.error}`;
            } else if (data.error.includes('must be a PDF') || data.error.includes('must be a PDF, JPEG')) {
              errorMessage = `File Type Error: ${data.error}`;
            } else if (data.error.includes('validation failed')) {
              // Handle Mongoose validation errors
              const validationErrors = {};
              if (data.details && Array.isArray(data.details)) {
                data.details.forEach(detail => {
                  const field = detail.path;
                  validationErrors[field] = detail.message;
                });
              } else if (data.error) {
                // If details is not an array, parse the error message
                const errorMsg = data.error;
                // Extract field names from error message
                const fieldMatches = errorMsg.match(/(\w+):\s*([^,]+)/g);
                if (fieldMatches) {
                  fieldMatches.forEach(match => {
                    const [field, ...messageParts] = match.split(':');
                    validationErrors[field.trim()] = messageParts.join(':').trim();
                  });
                }
              }
              const validationMessage = buildErrorMessage(validationErrors);
              setNetworkError(`Validation errors:\n\n${validationMessage}`);
              setOpenErrorDialog(true); // show the dialog before returning
              setIsSubmitting(false);
              return; // Early return for validation errors
            } else {
              errorMessage = data.error || 'Invalid request. Please check your input and try again.';
            }
          } else {
            errorMessage = 'Invalid request. Please check your input and try again.';
          }
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (status === 413) {
          errorMessage = 'File too large. Maximum file size is 5MB.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later or contact support if the problem persists.';
        } else if (data.error) {
          if (data.error.includes('duplicate key') || data.error.includes('E11000')) {
            // Handle duplicate key error - generate new token for retry
            const newToken = uuidv4();
            sessionStorage.setItem('submissionToken', newToken);
            setSubmissionToken(newToken);
            errorMessage = 'A duplicate submission was detected. Please try submitting again.';
          } else {
            errorMessage = data.error || 'An unexpected error occurred.';
          }
        }
      } else if (error.request) {
        // Request was made but no response received
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorMessage = 'Request timeout. The server took too long to respond. Please try again.';
        } else {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        }
      } else if (error.code === 'ERR_NETWORK') {
        // CORS or network issues
        errorMessage = 'Network error: Unable to connect to the server. This may be due to a CORS issue or server being unavailable. Please try again or contact support.';
      }
      
      setNetworkError(errorMessage);
      setOpenErrorDialog(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Compute panchayath options from the selected assembly.
  const selectedAssembly = assemblies.find(a => a._id === formData.legislativeAssembly);
  const municipalityOptions = selectedAssembly ? selectedAssembly.municipalities : [];

  return (
    <Container maxWidth="xl">
      <Paper elevation={3} sx={{ p: 3, mt: 7, mb: 4, position: 'relative' }}>
        <form onSubmit={handleSubmit}>
          {/* Top Action Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Tooltip title="Go Back">
              <IconButton color="primary" onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h5" sx={{ flexGrow: 1, textAlign: 'center' }}>
              Add New Complaint
            </Typography>
          </Box>

          {/* Form Content */}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Section 1: Personal Information */}
              <Grid item xs={12}>
                <Typography variant="h6">Personal Information</Typography>
                <Box sx={{ borderBottom: '1px solid #e0e0e0', mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('name')}
                  required
                  variant="outlined"
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name of Father/Mother (Optional)"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleChange}
                  onBlur={() => handleBlur('parentName')}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.parentName}
                  helperText={errors.parentName}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>

              {/* Section 2: Location & Contact Information */}
              <Grid item xs={12}>
                <Typography variant="h6">Location & Contact Information</Typography>
                <Box sx={{ borderBottom: '1px solid #e0e0e0', mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required variant="outlined" margin="normal" error={!!errors.legislativeAssembly}>
                  <InputLabel>Legislative Assembly</InputLabel>
                  <Select
                    label="Legislative Assembly"
                    name="legislativeAssembly"
                    value={formData.legislativeAssembly}
                    onChange={handleAssemblyChange}
                    onBlur={() => handleBlur('legislativeAssembly')}
                  >
                    <MenuItem value="">Select Legislative Assembly</MenuItem>
                    {assemblies.map((assembly) => (
                      <MenuItem key={assembly._id} value={assembly._id}>
                        {assembly.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.legislativeAssembly}</FormHelperText>
                </FormControl>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel>District (Optional)</InputLabel>
                  <Select
                    label="District (Optional)"
                    name="district"
                    value={formData.district ? String(formData.district) : ''}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Select District</MenuItem>
                    {Array.isArray(districts) && districts.map((district) => (
                      <MenuItem key={district._id} value={String(district._id)}>
                        {district.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Ward No (Optional)"
                  name="wardNo"
                  value={formData.wardNo}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Address (Optional)"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  onBlur={() => handleBlur('address')}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.address}
                  helperText={errors.address}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 700 }}
                />
                <TextField
                  fullWidth
                  label="Mobile Number (Optional)"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  onBlur={() => handleBlur('mobileNumber')}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.mobileNumber}
                  helperText={errors.mobileNumber}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required variant="outlined" margin="normal" error={!!errors.panchayath}>
                  <InputLabel>Panchayath</InputLabel>
                  <Select
                    label="Panchayath"
                    name="panchayath"
                    value={formData.panchayath}
                    onChange={handleChange}
                    onBlur={() => handleBlur('panchayath')}
                    disabled={!formData.legislativeAssembly}
                  >
                    <MenuItem value="">Select Panchayath</MenuItem>
                    {municipalityOptions.map((muni, index) => (
                      <MenuItem key={index} value={muni.name}>
                        {muni.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.panchayath}</FormHelperText>
                </FormControl>
                <TextField
                  fullWidth
                  label="Alternate Mobile (Optional)"
                  name="alternateMobileNumber"
                  value={formData.alternateMobileNumber}
                  onChange={handleChange}
                  onBlur={() => handleBlur('alternateMobileNumber')}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.alternateMobileNumber}
                  helperText={errors.alternateMobileNumber}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              {/* Section 3: Complaint & ID Details */}
              <Grid item xs={12}>
                <Typography variant="h6">Complaint & ID Details</Typography>
                <Box sx={{ borderBottom: '1px solid #e0e0e0', mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required variant="outlined" margin="normal" error={!!errors.category}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    onBlur={() => handleBlur('category')}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    {category.map((cat) => (
                      <MenuItem key={cat._id} value={cat.name}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.category}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined" margin="normal" error={!!errors.priority}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    label="Priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    onBlur={() => handleBlur('priority')}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Urgent">Urgent</MenuItem>
                  </Select>
                  <FormHelperText>{errors.priority}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined" margin="normal" error={!!errors.idProofType}>
                  <InputLabel>ID Proof Type (Optional)</InputLabel>
                  <Select
                    label="ID Proof Type (Optional)"
                    name="idProofType"
                    value={formData.idProofType}
                    onChange={handleChange}
                    onBlur={() => handleBlur('idProofType')}
                  >
                    <MenuItem value="">Select ID Proof</MenuItem>
                    <MenuItem value="Aadhaar">Aadhaar</MenuItem>
                    <MenuItem value="DL">DL</MenuItem>
                    <MenuItem value="Election ID">Election ID</MenuItem>
                    <MenuItem value="Passport">Passport</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  <FormHelperText>{errors.idProofType}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ID Number (Optional)"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  onBlur={() => handleBlur('idNumber')}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.idNumber}
                  helperText={errors.idNumber}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Reference (Optional)"
                  name="reference"
                  value={formData.reference}
                  onChange={handleChange}
                  onBlur={() => handleBlur('reference')}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.reference}
                  helperText={errors.reference}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Referred Mobile (Optional)"
                  name="referredMobileNumber"
                  value={formData.referredMobileNumber}
                  onChange={handleChange}
                  onBlur={() => handleBlur('referredMobileNumber')}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.referredMobileNumber}
                  helperText={errors.referredMobileNumber}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onBlur={() => handleBlur('subject')}
                  required
                  variant="outlined"
                  margin="normal"
                  error={!!errors.subject}
                  helperText={errors.subject}
                />
              </Grid>

              {/* Section 4: Complaint Details (Rich Text Editor) */}
              <>
                <style>{`
                  .custom-quill .ql-container {
                    min-height: 200px;
                  }
                  .custom-quill .ql-editor {
                    min-height: 200px;
                  }
                `}</style>
                <Grid item xs={12}>
                  <Typography variant="h6">Complaint Details</Typography>
                  <Box sx={{ mb: 2 }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.complaintDetails || ''}
                      onChange={handleComplaintDetailsChange}
                      className="custom-quill"
                      style={{ marginBottom: '10px' }}
                    />
                    {errors.complaintDetails && (
                      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                        {errors.complaintDetails}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </>

              {/* Section 5: Remarks */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks (Optional)"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  onBlur={() => handleBlur('remarks')}
                  multiline
                  rows={3}
                  variant="outlined"
                  margin="normal"
                  error={!!errors.remarks}
                  helperText={errors.remarks}
                  inputProps={{ maxLength: 3000 }}
                />
              </Grid>

              {/* Section 6: File Uploads */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* ID Proof Upload */}
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{
                        mt: 2,
                        mb: 1,
                        height: 56,
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                        }
                      }}
                      startIcon={<UploadFileIcon />}
                    >
                      Upload ID Proof
                      <input
                        type="file"
                        name="idProofFile"
                        hidden
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {errors.idProofFile && (
                      <Typography color="error" variant="body2">
                        {errors.idProofFile}
                      </Typography>
                    )}
                    {idProofPreview && (
                      <Box sx={{ mt: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="body2" color="success.main">
                          ✓ ID Proof uploaded successfully
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {/* Complaint PDF Upload */}
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{
                        mt: 2,
                        mb: 1,
                        height: 56,
                        background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
                        }
                      }}
                      startIcon={<UploadFileIcon />}
                    >
                      Upload Complaint PDF
                      <input
                        type="file"
                        name="complaintPdfFile"
                        hidden
                        accept=".pdf"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {errors.complaintPdfFile && (
                      <Typography color="error" variant="body2">
                        {errors.complaintPdfFile}
                      </Typography>
                    )}
                    {complaintPdfPreview && (
                      <Box sx={{ mt: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="body2" color="success.main">
                          ✓ Complaint PDF uploaded successfully
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Section 7: Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #bdbdbd 0%, #e0e0e0 100%)',
                        transform: 'none',
                        boxShadow: 'none',
                      }
                    }}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Complaint'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </form>

        {/* Enhanced Error Dialog */}
        <Dialog
          open={openErrorDialog}
          onClose={() => setOpenErrorDialog(false)}
          aria-labelledby="error-dialog-title"
          aria-describedby="error-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="error-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            Validation Errors
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="error-dialog-description">
              <Alert severity="error" sx={{ mb: 2 }}>
                Please fix the following validation errors before submitting:
              </Alert>
              <List>
                {Object.entries(errors).map(([field, error]) => (
                  <ListItem key={field} sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <WarningIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {field.charAt(0).toUpperCase() + field.replace(/([A-Z])/g, ' $1').toLowerCase()}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="error">
                          {error}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenErrorDialog(false)} color="primary" variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Duplicate Complaint Warning Dialog */}
        <Dialog
          open={openDuplicateDialog}
          onClose={handleDuplicateCancel}
          aria-labelledby="duplicate-dialog-title"
          aria-describedby="duplicate-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="duplicate-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Possible duplicate complaint
          </DialogTitle>
          <DialogContent>
            {duplicateInfo && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }} id="duplicate-dialog-description">
                  This mobile number has {duplicateInfo.matches.length} open complaint(s) in the last {duplicateInfo.windowDays} days.
                  Please review them below before submitting a new complaint.
                </Alert>
                <List dense>
                  {duplicateInfo.matches.map((match) => (
                    <ListItem key={match._id} sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <InfoIcon color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <Typography
                              component="span"
                              variant="body2"
                              color="primary"
                              sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
                              onClick={() => navigate(`/complaint/${match._id}`)}
                            >
                              {match.complaintId}
                            </Typography>
                            {' — '}{match.subject}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            Status: {match.status} | Created: {new Date(match.createdAt).toLocaleDateString()}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDuplicateCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDuplicateSubmitAnyway} color="primary" variant="contained">
              Submit Anyway
            </Button>
          </DialogActions>
        </Dialog>

        {/* SMS Status Dialog */}
        <Dialog
          open={openSmsStatusDialog}
          onClose={() => {
            setOpenSmsStatusDialog(false);
            navigate(localStorage.getItem('role') === 'admin' ? '/admin' : '/complaints');
          }}
          aria-labelledby="sms-status-dialog-title"
          aria-describedby="sms-status-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="sms-status-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MessageIcon color="primary" />
            SMS Status
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="sms-status-dialog-description">
              {smsStatus ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="h6" color="success.main">
                      Complaint Submitted Successfully
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {smsStatus.success ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                    <Typography variant="body1" color={smsStatus.success ? 'success.main' : 'error.main'}>
                      <strong>SMS Status:</strong> {smsStatus.success ? 'Sent Successfully' : 'Failed to Send'}
                    </Typography>
                  </Box>
                  
                  {smsStatus.message && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {smsStatus.message}
                    </Typography>
                  )}
                  
                  {smsStatus.error && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ErrorIcon color="error" fontSize="small" />
                      <Typography variant="body2" color="error">
                        Error: {smsStatus.error}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Show detailed SMS status if available */}
                  {smsStatus.latestStatus && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Latest SMS Details:</strong>
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Confirmation SMS:</strong> {(smsStatus.latestStatus.confirmation?.status || 'not_attempted').toUpperCase()}
                          </Typography>
                          {smsStatus.latestStatus.confirmation?.sentAt && (
                            <Typography variant="body2" color="text.secondary">
                              Sent: {new Date(smsStatus.latestStatus.confirmation.sentAt).toLocaleString()}
                            </Typography>
                          )}
                          {smsStatus.latestStatus.confirmation?.error && (
                            <Typography variant="body2" color="error">
                              Error: {smsStatus.latestStatus.confirmation.error}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2">
                            <strong>Status Update SMS:</strong> {(smsStatus.latestStatus.statusUpdate?.status || 'not_attempted').toUpperCase()}
                          </Typography>
                          {smsStatus.latestStatus.statusUpdate?.sentAt && (
                            <Typography variant="body2" color="text.secondary">
                              Sent: {new Date(smsStatus.latestStatus.statusUpdate.sentAt).toLocaleString()}
                            </Typography>
                          )}
                          {smsStatus.latestStatus.statusUpdate?.error && (
                            <Typography variant="body2" color="error">
                              Error: {smsStatus.latestStatus.statusUpdate.error}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    {smsStatus.success 
                      ? '✓ A confirmation SMS has been sent to your registered mobile number.'
                      : '⚠ Your complaint has been recorded but SMS notification failed. You can check the status later.'
                    }
                  </Typography>
                </Box>
              ) : (
                'SMS status information not available.'
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {smsStatus && smsStatus.latestStatus && (
              <Button 
                onClick={() => refreshSmsStatus(smsStatus.latestStatus.complaintId)}
                disabled={smsRefreshLoading}
                startIcon={smsRefreshLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                variant="outlined"
              >
                {smsRefreshLoading ? 'Refreshing...' : 'Refresh Status'}
              </Button>
            )}
            <Button
              onClick={() => {
                setOpenSmsStatusDialog(false);
                navigate(localStorage.getItem('role') === 'admin' ? '/admin' : '/complaints');
              }}
              color="primary"
              variant="contained"
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default ComplaintForm;
